import { LaunchBackgroundTaskServiceInstanceOptions } from '../../../services/webServiceRouting/types';
import { getServices } from '../../../infra/startup';
import IBackgroundTaskService, {
  Task
} from '../../../services/backgroundTaskService/backgroundTask/IBackgroundTaskService';
import bindAllMethods from '../../../utils/bindAllMethods';
import * as T from './types';
import { OnboardingInputType } from '../../../services/userOnboardingService/types';
import WebServiceRouting from 'src/services/webServiceRouting';

class ServiceRoutingInterfaceV1 {
  private _webServiceRouting: WebServiceRouting; // WebServiceRouting instance
  private _backgroundTaskService: IBackgroundTaskService; // BackgroundTaskManagerService instance
  private _onboardingInput: OnboardingInputType;

  constructor(params: { onboardingInput: OnboardingInputType }) {
    const services = getServices();
    this._webServiceRouting = services.webServiceRouting;
    this._backgroundTaskService = services.backgroundTaskService;
    this._onboardingInput = params.onboardingInput;
    bindAllMethods(this);
  }

  private isEnabled(): boolean {
    return this._onboardingInput?.enable;
  }

  async getServiceInstanceLaunchOptions(): Promise<
    T.LaunchServiceOptions | T.ServiceRoutingError
  > {
    const serviceInstanceLaunchOptions =
      await this._webServiceRouting.getServiceInstanceLaunchOptions();

    return this._webServiceRouting.separateErrorObject(
      serviceInstanceLaunchOptions
    )?.data;
  }

  async launchService(launchOptions: {
    /**
     * The identifier of the service that should be launched
     */
    serviceId: string;
    /**
     * Options specific to the service being launched, if any are required
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    serviceOptions?: Record<string, any>;
  }): Promise<void> {
    if (this.isEnabled()) {
      await this._webServiceRouting.launchService({
        serviceId: launchOptions?.serviceId,
        serviceOptions: launchOptions?.serviceOptions
      });
    }
  }

  async closeServiceInstance(
    payload: T.ServiceRoutingCloseServiceInstancePropType
  ): Promise<void> {
    if (this.isEnabled()) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this._webServiceRouting.closeServiceInstance(payload as any);
    }
  }

  async getServices(): Promise<{
    services: T.InternalServiceObjectType[];
  }> {
    const response = this._webServiceRouting.separateErrorObject(
      await this._webServiceRouting.getServices()
    )?.data;

    return response;
  }

  async launchBackgroundTasks(
    launchOptions: LaunchBackgroundTaskServiceInstanceOptions[]
  ): Promise<void> {
    return this._webServiceRouting.launchBackgroundTasksService(launchOptions);
  }

  async registerBackgroundTask(id: string, task: Task): Promise<void> {
    await this._backgroundTaskService.register(id, task);
  }

  public getInterface(): T.ServiceRoutingInterfaceType {
    return {
      launchService: this.launchService,
      getServiceInstanceLaunchOptions: this.getServiceInstanceLaunchOptions,
      closeServiceInstance: this.closeServiceInstance,
      getServices: this.getServices,
      registerBackgroundTask: this.registerBackgroundTask,
      launchBackgroundTasks: this.launchBackgroundTasks
    };
  }
}

export default ServiceRoutingInterfaceV1;
